import { ConnectError } from "@connectrpc/connect";
import { redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import toast from "react-hot-toast";
import type { CreditCard } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import ArrowBack from "~icons/material-symbols/arrow-back";
import ProgressActivity from "~icons/material-symbols/progress-activity";
import { css, cx } from "../../../../styled-system/css";
import Header from "../../../components/header";
import {
  CardBrand,
  formatMaskedCardNo,
} from "../../../features/charge/card-ui";
import { getCreditCardsOrEmpty } from "../../../features/charge/credit-card-loader";
import { showGMOPGErrorToast } from "../../../features/charge/gmopg-error-toast";
import {
  labelCss,
  pageCss,
  primaryButtonCss,
  secondaryButtonCss,
} from "../../../features/charge/styles";
import { getGMOPGErrorInfoFromUnknown } from "../../../libs/connect";
import { t } from "../../../libs/i18n";

type ChargeSearch = {
  amount?: number;
  // 遷移元。カード一覧へ戻る際に設定画面由来かどうかを引き継ぐ
  from?: "settings";
};

type LoaderData = {
  card: CreditCard | null;
  defaultGmoCardId?: string;
};

export const Route = createFileRoute(
  "/_authed/charge/credit-card/manage_/$gmoCardId",
)({
  validateSearch: (search: Record<string, unknown>): ChargeSearch => ({
    amount: Number(search.amount) === 10000 ? 10000 : 5000,
    from: search.from === "settings" ? "settings" : undefined,
  }),
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.charge) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context: { client }, params: { gmoCardId } }) => {
    const { response, recoveredFromGMOPGError } =
      await getCreditCardsOrEmpty(client);
    const card = Object.values(response.cards).find(
      (candidate) => candidate.gmoCardId === gmoCardId,
    );

    if (!card) {
      if (!recoveredFromGMOPGError) {
        throw redirect({ to: "/charge/credit-card/manage" });
      }
      return {
        card: null,
        defaultGmoCardId: response.defaultGmoCardId,
      } satisfies LoaderData;
    }

    return {
      card,
      defaultGmoCardId: response.defaultGmoCardId,
    };
  },
  component: CreditCardDetail,
});

function CreditCardDetail() {
  const { card, defaultGmoCardId } = Route.useLoaderData();
  const { amount = 5000, from } = Route.useSearch();
  const { client } = Route.useRouteContext();
  const navigate = useNavigate();
  const router = useRouter();
  const [updatingDefault, setUpdatingDefault] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isDefault = card ? defaultGmoCardId === card.gmoCardId : false;

  const backToList = () =>
    navigate({ to: "/charge/credit-card/manage", search: { amount, from } });

  const setDefault = async () => {
    if (isDefault || !card) {
      return;
    }

    setUpdatingDefault(true);
    try {
      await client.setDefaultCreditCard({ gmoCardId: card.gmoCardId });
      toast.success(t("メインカードに設定しました"));
      await router.invalidate();
    } catch (error) {
      const gmoError = getGMOPGErrorInfoFromUnknown(error);
      if (gmoError) {
        showGMOPGErrorToast(gmoError);
        return;
      }
      showCardManagementError(error, t("メインカードの設定に失敗しました"));
    } finally {
      setUpdatingDefault(false);
    }
  };

  const deleteCard = async () => {
    if (!card) {
      return;
    }

    setDeleting(true);
    try {
      await client.deleteCreditCard({ gmoCardId: card.gmoCardId });
      toast.success(t("クレジットカードを削除しました"));
      await backToList();
    } catch (error) {
      const gmoError = getGMOPGErrorInfoFromUnknown(error);
      if (gmoError) {
        showGMOPGErrorToast(gmoError);
        return;
      }
      showCardManagementError(error, t("クレジットカードの削除に失敗しました"));
    } finally {
      setDeleting(false);
    }
  };

  const disabled = updatingDefault || deleting || !card;

  return (
    <>
      <Header title={t("クレジットカード管理")} onClickBack={backToList} />
      <main className={pageCss}>
        {card ? (
          <section
            className={css({
              bg: "surface.primary",
              border: "1px solid",
              borderColor: "border.secondary",
              rounded: "16px",
              px: "20px",
              py: "8px",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
            })}
          >
            <DetailRow label={t("名義人")} showDivider>
              {card.holderName || "-"}
            </DetailRow>
            <DetailRow label={t("カード番号")} showDivider>
              {formatMaskedCardNo(card.maskedCardNo)}
            </DetailRow>
            <DetailRow label={t("ブランド")} showDivider>
              <CardBrand brand={card.cardBrand} />
            </DetailRow>
            <DetailRow label={t("有効期限")} showDivider>
              {card.expiryMonth}/{card.expiryYear}
            </DetailRow>
            <DetailRow label={t("セキュリティコード")}>***</DetailRow>
          </section>
        ) : (
          <p
            className={css({
              color: "text.secondary",
              fontSize: "14px",
              lineHeight: "1.7",
            })}
          >
            {t("カード情報を表示できません。")}
          </p>
        )}

        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          })}
        >
          <button
            type="button"
            className={primaryButtonCss}
            disabled={disabled || isDefault}
            onClick={setDefault}
          >
            {updatingDefault ? (
              <ProgressActivity
                className={css({ animation: "spin 1s linear infinite" })}
              />
            ) : (
              t("メインカードにする")
            )}
          </button>
          <button
            type="button"
            className={cx(secondaryButtonCss, css({ color: "#D32F2F" }))}
            disabled={disabled}
            onClick={deleteCard}
          >
            {deleting ? (
              <ProgressActivity
                className={css({ animation: "spin 1s linear infinite" })}
              />
            ) : (
              t("カードを削除")
            )}
          </button>
          <button
            type="button"
            className={secondaryButtonCss}
            disabled={disabled}
            onClick={backToList}
          >
            <ArrowBack />
            {t("もどる")}
          </button>
        </div>
      </main>
    </>
  );
}

function DetailRow({
  label,
  showDivider,
  children,
}: {
  label: string;
  showDivider?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={css({
        minH: "54px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        borderBottom: showDivider ? "1px solid" : undefined,
        borderColor: showDivider ? "border.secondary" : undefined,
      })}
    >
      <span className={labelCss}>{label}</span>
      <span
        className={css({
          minW: 0,
          display: "flex",
          justifyContent: "flex-end",
          color: "text.primary",
          fontSize: "16px",
          lineHeight: "1.5",
          textAlign: "right",
          overflowWrap: "anywhere",
        })}
      >
        {children}
      </span>
    </div>
  );
}

function showCardManagementError(error: unknown, message: string) {
  if (error instanceof ConnectError) {
    toast.error(`${message}\n[${error.code}]`);
    return;
  }
  if (error instanceof Error) {
    toast.error(`${message}\n[${error.message}]`);
    return;
  }
  toast.error(message);
}
